import { useState, ChangeEvent } from 'react';
import { useQuery } from '@apollo/client';

import usePagination from '@hooks/usePagination';
import { operatorTableHeader } from '@lib/data';
import { SEE_ADMIN_LIST } from '@graphql/queries';
import { Query, Admin } from '@graphql/types';

import { FilterBox, Contents } from '@components/styled/StyledComponents';
import Headline from '@components/layout/Headline';
import StyledSelect from '@components/styled/StyledSelect';
import TableHeader from '@components/share/Table/TableHeader';
import { TableBody, EmptyTable } from '@components/share/Table/table.style';
import OperatorListTableRow from '@components/feature/Setting/Sysop/OperatorListTableRow';
import OperatorModal from '@components/feature/Setting/Sysop/OperatorModal';
import Pagination from '@components/layout/Pagination';
import Loader from '@components/share/Loader';

const SysopList = () => {
    const [addModal, setAddModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    // 페이지네이션 관련 상태
    const [take, setTake] = useState(10);
    const [preCursor, setPreCursor] = useState(0);
    const [nextCursor, setNextCursor] = useState(0);
    const [cursorOption, setCursorOption] = useState<boolean | null>(null);
    const [filter, setFilter] = useState({ order: 1 });

    const { data, loading } = useQuery<Pick<Query, 'seeAdminList'>>(
        SEE_ADMIN_LIST,
        {
            variables: {
                searchTerm,
                orderBy: +filter.order,
                take,
                preCursor,
                nextCursor,
                cursorOption
            },
            onError: (err) => {
                console.error(err);
            }
        }
    );

    const adminList = data?.seeAdminList?.adminList ?? [];
    const totalLength = data?.seeAdminList?.totalLength ?? 0;

    const onFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilter((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // 페이지네이션
    const {
        pageIndex,
        setPageIndex,
        pageGroupLength,
        prevPageHandler,
        nextPageHandler,
        prevPageGroupHandler,
        nextPageGroupHandler,
        resetPaginationHandler,
        disabled
    } = usePagination(
        take,
        totalLength,
        setPreCursor,
        setNextCursor,
        setCursorOption,
        adminList,
        'adminId'
    );

    return (
        <Contents>
            <Headline
                title="운영자 관리"
                subTitle="펫이닷 운영자 목록 조회 및 등록"
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchCount={totalLength}
                buttonList={['운영자 추가']}
                buttonAction={[() => setAddModal(true)]}
                SEARCH
            />
            {/*  필터 영역 */}
            <FilterBox>
                <StyledSelect
                    title="정렬"
                    name="order"
                    value={filter.order}
                    onChange={onFilterChange}
                    options={[
                        { title: '내림차순', value: 1 },
                        { title: '오름차순', value: 2 }
                    ]}
                    DATA_VALUE
                />
            </FilterBox>
            <TableHeader header={operatorTableHeader} />

            <TableBody $minHeight={500}>
                {loading && <Loader />}
                {totalLength === 0 ? (
                    <EmptyTable>목록이 없습니다.</EmptyTable>
                ) : (
                    adminList
                        .slice(pageIndex, pageIndex + take)
                        .map((data, idx) => (
                            <OperatorListTableRow
                                key={`${data?.adminId}-operator-list`}
                                header={operatorTableHeader}
                                data={data as Admin}
                            />
                        ))
                )}
            </TableBody>

            {totalLength > 0 && (
                <Pagination
                    take={take}
                    setTake={setTake}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    pageGroupLength={pageGroupLength}
                    totalLength={totalLength}
                    prevPageHandler={prevPageHandler}
                    nextPageHandler={nextPageHandler}
                    prevPageGroupHandler={prevPageGroupHandler}
                    nextPageGroupHandler={nextPageGroupHandler}
                    resetPaginationHandler={resetPaginationHandler}
                    disabled={disabled}
                />
            )}
            {addModal && (
                <OperatorModal isOpen={addModal} setIsOpen={setAddModal} />
            )}
        </Contents>
    );
};

export default SysopList;
